import { CheckCircle2, Circle, LogOut, ArrowLeft, Home } from 'lucide-react';
import type { Task } from '../App';
import type { Project } from '../types/project';
import { useState } from 'react';

interface TaskSelectionProps {
  completedTasks: string[];
  customTasks: Task[];
  categoryName?: string;
  activeProject?: Project;
  projects?: Project[];
  activeProjectId?: string;
  onSelectTask: (taskName: string) => void;
  onSelectProject?: (projectId: string) => void;
  onBack?: () => void;
  onGoHome?: () => void;
  onLogout: () => void;
}

export function TaskSelection({
  completedTasks,
  customTasks,
  categoryName,
  activeProject,
  projects = [],
  activeProjectId,
  onSelectTask,
  onSelectProject,
  onBack,
  onGoHome,
  onLogout
}: TaskSelectionProps) {
  // Safety check - ensure tasks exist
  if (!customTasks || customTasks.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              {onGoHome && (
                <button
                  onClick={onGoHome}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md bg-white/80"
                >
                  <Home className="w-5 h-5" />
                  <span>Home</span>
                </button>
              )}
              {onBack && (
                <button
                  onClick={onBack}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md bg-white/80"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Back to Categories</span>
                </button>
              )}
              {!onBack && !onGoHome && <div />}
            </div>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md bg-white/80"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Tasks Available</h2>
            <p className="text-gray-600">There are no tasks in this category for the selected project.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Top Navigation */}
        <div className="flex justify-between items-center mb-6">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md bg-white/80"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Categories</span>
            </button>
          )}
          {!onBack && <div />}
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md bg-white/80"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>


        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {categoryName ? `${categoryName} Tasks` : 'What tasks were accomplished today?'}
          </h1>
          <p className="text-gray-600">Select a task to fill in today's report</p>
        </div>

        {/* Task List */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="space-y-3">
            {customTasks.map((task, index) => {
              const isCompleted = completedTasks.includes(task.id);
              
              return (
                <button
                  key={`${task.id}-${index}`}
                  onClick={() => onSelectTask(task.id)}
                  className={`w-full flex items-center justify-between px-6 py-4 border rounded-lg transition duration-200 group ${
                    isCompleted
                      ? 'bg-green-50 border-green-200 hover:bg-green-100'
                      : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
                  }`}
                >
                  <span className="text-gray-900 font-medium text-left">{task.title}</span>
                  
                  {isCompleted ? (
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                  ) : (
                    <Circle className="w-6 h-6 text-gray-400 group-hover:text-indigo-400 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
}